import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client';

export type AgeBand = 'MINOR' | 'ADULT';
// Server: auth/AuthController.java's own `AccountView` record. `mustRename` (Decision #10's
// grandfather flow) is true only for a pre-existing account whose display name collided with
// another account's once uniqueness was enforced — see docs/RULINGS.md and account/RenameService.
// `role` (Phase 7 Task 11) is present ONLY for a staff account (`@JsonInclude(NON_NULL)` server-
// side) — `undefined` here for every player, never a literal `"player"`/`null` value; StaffGate.tsx
// is the sole consumer (`account?.role === 'staff'`).
// `battleEffects` (#14.2 / R1, 2026-09-27): the real server always sends `'cosy'` or `'splatter'`
// (`accounts.battle_effects`, default `cosy`) — optional here only because most of this suite's
// `/me` fixtures predate the field and don't care about it; `fx/gorePrefs.ts`'s `useGorePref` is
// the sole reader, and treats an absent value the same as `'cosy'`.
export type AccountView = {
  id: string; displayName: string; ageBand: AgeBand | 'UNKNOWN'; mustRename: boolean; role?: 'staff';
  battleEffects?: 'cosy' | 'splatter';
};

/**
 * C3: the birth date exists only inside this function. Only the band is transmitted.
 *
 * `birthDate` is parsed by splitting its numeric components rather than via `new Date(str)`:
 * a bare "YYYY-MM-DD" string is parsed as UTC midnight by the Date constructor, but the
 * comparison below reads `today`'s local getters — west of UTC, that mismatch rolls the
 * parsed birth date back a calendar day and classifies a user ADULT a day early.
 */
export function deriveAgeBand(birthDate: string, today: Date = new Date()): AgeBand {
  const [by, bm, bd] = birthDate.split('-').map(Number);
  const ty = today.getFullYear();
  const tm = today.getMonth() + 1;
  const td = today.getDate();
  let age = ty - by;
  const beforeBirthday = tm < bm || (tm === bm && td < bd);
  if (beforeBirthday) age -= 1;
  return age >= 18 ? 'ADULT' : 'MINOR';
}

export const register = (input: { email: string; password: string; displayName: string; ageBand: AgeBand }) =>
  apiFetch<AccountView>('/auth/register', { method: 'POST', body: JSON.stringify(input) });
export const login = (input: { email: string; password: string }) =>
  apiFetch<AccountView>('/auth/login', { method: 'POST', body: JSON.stringify(input) });
export const logout = () => apiFetch<void>('/auth/logout', { method: 'POST' });
export const me = () => apiFetch<AccountView>('/me');

// Every screen that reads `/me` today (MeScreen, NotificationSettingsScreen, AppShell) uses this
// same literal key by hand rather than a shared factory — `meKeys.detail` exists so NEW Phase 6
// code (ClaimHandleScreen, this module's own useRenameDisplayName below) can reference it by name
// instead of a second magic-string copy, while staying byte-identical to the existing literal
// (TanStack Query keys compare by deep-equality, not reference, so both forms share one cache
// entry regardless).
export const meKeys = { detail: ['me'] as const };

// PATCH /me/display-name {displayName,idempotencyKey} — Decision #10's rename surface
// (AuthController.renameDisplayName / account/RenameService). idempotencyKey is accepted but
// currently unused server-side (see RenameService's own javadoc: a rename has no value that can
// double-apply from a retry) — sent anyway so every write endpoint gets one consistently, same
// reasoning that javadoc gives. Refusals: name_taken (409, NameTakenException), validation (400,
// "Invalid field: displayName" — NameRule), text_rejected (400, the word filter — never echoes
// the matched term, R5).
export const renameDisplayName = (input: { displayName: string; idempotencyKey: string }) =>
  apiFetch<AccountView>('/me/display-name', { method: 'PATCH', body: JSON.stringify(input) });

/**
 * PATCH /me/battle-effects {battleEffects} — #14.2 / R1 (2026-09-27): the client-side gore
 * preference (`projectpets.fx.gore`) is retired; battle effects (gore bursts, the thrown blood
 * splash, permanent stains) are opt-in and stored on the account (`accounts.battle_effects`),
 * because stains accrue there too. An unknown value 400s. `fx/gorePrefs.ts`'s `useGorePref` is
 * the sole caller.
 */
export const setBattleEffects = (battleEffects: 'cosy' | 'splatter') =>
  apiFetch<AccountView>('/me/battle-effects', { method: 'PATCH', body: JSON.stringify({ battleEffects }) });

/**
 * On success, writes the fresh AccountView straight into the shared `['me']` cache (no refetch
 * round-trip — same `setQueryData`-on-mutation-response idiom arena.ts's `useSetParty` and
 * shops.ts's `usePurchase` already use) so every screen reading `useQuery(['me'])` sees
 * `mustRename: false` immediately, and ClaimHandleScreen's own caller updates the mustRename
 * latch itself from this same response (see that screen for why: the latch lives in a zustand
 * store outside React Query, not derivable from cache alone).
 */
export function useRenameDisplayName() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (displayName: string) => renameDisplayName({ displayName, idempotencyKey: crypto.randomUUID() }),
    onSuccess: (data) => queryClient.setQueryData(meKeys.detail, data),
  });
}
